<template>
  <div class="tutoria-chat-container">
    <!-- Header -->
    <div class="tutoria-chat-header">
      <div class="tutoria-chat-header-info">
        <div class="tutoria-avatar">
          <TutorIASVG class="tutoria-avatar-icon" />
        </div>
        <div>
          <h3 class="tutoria-title">CyberTutor <sup>(beta)</sup></h3>
          <p class="tutoria-status" :class="{ 'connected': isConnected }">
            {{ connectionStatus }}
          </p>
        </div>
      </div>
      <button @click="$emit('close')" class="tutoria-close-btn">
        <span>×</span>
      </button>
    </div>

    <!-- Messages -->
    <div ref="messagesContainer" class="tutoria-messages">
      <!-- Not authenticated message -->
      <div v-if="!myToken" class="tutoria-not-authenticated">
        <div class="tutoria-not-authenticated-icon">🔒</div>
        <h3>Autenticação Necessária</h3>
        <p>Você precisa estar autenticado para usar o CyberTutor.</p>
        <p>Por favor, faça login para acessar a assistente virtual.</p>
      </div>
      
      <div v-else-if="conversationHistory.length === 0" class="tutoria-empty-state">
        <p>👋 Olá! Sou a CyberTutor, sua assistente virtual.</p>
        <p>Como posso ajudar você hoje?</p>
      </div>

      <div
        v-for="(message, index) in conversationHistory"
        :key="index"
        class="tutoria-message"
        :class="message.role"
      >
        <div class="tutoria-message-content">
          <div class="tutoria-message-text" v-html="formatMessage(message.content)"></div>
          <div v-if="message.timestamp" class="tutoria-message-time">
            {{ formatTime(message.timestamp) }}
          </div>
          
          <!-- Vídeos recomendados -->
          <div v-if="message.recommended_videos && message.recommended_videos.length > 0" class="tutoria-recommended-videos">
            <p class="tutoria-recommended-title">📹 Vídeos Recomendados:</p>
            <div
              v-for="(video, vIndex) in getUniqueVideos(message.recommended_videos)"
              :key="vIndex"
              class="tutoria-video-item"
            >
              <a :href="video.course_url" target="_blank">
                {{ video.course_name }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Typing indicator -->
      <div v-if="isTyping" class="tutoria-message assistant">
        <div class="tutoria-message-content">
          <div class="tutoria-typing-indicator">
            <span class="tutoria-typing-text">CyberTutor está consultando</span>
            <span class="tutoria-typing-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input -->
    <div class="tutoria-input-container">
      <form @submit.prevent="sendQuestion" class="tutoria-input-form">
        <textarea
          v-model="currentQuestion"
          @keydown.enter.exact.prevent="sendQuestion"
          :placeholder="myToken ? 'Digite sua pergunta...' : 'Faça login para usar o chat'"
          class="tutoria-input"
          rows="1"
          :disabled="!myToken || !isConnected || isTyping"
          maxlength="1000"
        ></textarea>
        <button
          type="submit"
          class="tutoria-send-btn"
          :disabled="!myToken || !isConnected || isTyping || !currentQuestion.trim()"
        >
          <span>&#10148;</span>
        </button>
      </form>
      <div class="tutoria-actions">
        <button
          @click="createNewSession"
          class="tutoria-action-btn"
          :disabled="!myToken || !isConnected"
        >
          🔄 Nova Sessão
        </button>
        <button
          @click="clearSession"
          class="tutoria-action-btn"
          :disabled="!myToken || !isConnected"
        >
          🗑️ Limpar Chat
        </button>
      </div>
      
      <!-- Toggle Mode Button -->
      <button
        v-if="showToggleButton"
        @click="$emit('toggle-mode')"
        class="tutoria-toggle-mode-btn"
        title="Alternar modo de visualização"
      >
        <span>⇄</span>
      </button>
    </div>
  </div>
</template>

<script>
import TutorIASVG from '@/components/svg/TutorIASVG.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'TutorIAChat',
  components: {
    TutorIASVG,
  },
  props: {
    currentClassId: {
      type: Number,
      default: null
    },
    wsUrl: {
      type: String,
      default: 'ws://localhost:8081'
    },
    showToggleButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      ws: null,
      isConnected: false,
      isTyping: false,
      currentQuestion: '',
      sessionId: null,
      conversationHistory: [],
      reconnectAttempts: 0,
      maxReconnectAttempts: 5
    }
  },
  computed: {
    ...mapGetters('user', ['token', 'isLogged']),
    
    myToken() {
      return this.token
    },
    
    connectionStatus() {
      if (!this.myToken) return 'Não autenticado'
      if (this.isConnected) return 'Conectado'
      if (this.reconnectAttempts > 0) return 'Reconectando...'
      return 'Desconectado'
    }
  },
  methods: {
    connectWebSocket() {
      if (!this.myToken) {
        console.warn('⚠️ Não é possível conectar: usuário não autenticado')
        return
      }
      
      try {
        this.ws = new WebSocket(this.wsUrl)

        this.ws.onopen = () => {
          console.log('WebSocket conectado')
          this.isConnected = true
          this.reconnectAttempts = 0
          this.authenticate()
        }

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleServerMessage(message)
          } catch (error) {
            console.error('Erro ao parsear mensagem:', error)
          }
        }

        this.ws.onerror = (error) => {
          console.error('Erro no WebSocket:', error)
        }

        this.ws.onclose = () => {
          console.log('WebSocket desconectado')
          this.isConnected = false
          
          if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++
            setTimeout(() => {
              console.log(`Tentativa de reconexão ${this.reconnectAttempts}/${this.maxReconnectAttempts}`)
              this.connectWebSocket()
            }, 3000 * this.reconnectAttempts)
          }
        }
      } catch (error) {
        console.error('Erro ao conectar WebSocket:', error)
      }
    },

    authenticate() {
      if (!this.ws || !this.myToken) {
        console.error('❌ Não é possível autenticar:', {
          hasWebSocket: !!this.ws,
          hasToken: !!this.myToken
        })
        return
      }

      console.log('🔐 Autenticando...')
      this.sendMessage({
        action: 'AUTHENTICATE',
        token: this.myToken,
        liveId: 'tutoria'
      })
    },

    sendMessage(data) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(data))
      } else {
        console.error('WebSocket não está conectado')
      }
    },

    handleAnswerScroll() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          const allMessages = this.$refs.messagesContainer.querySelectorAll('.tutoria-message')
          const lastMessageElement = allMessages[allMessages.length - 1]
          
          if (lastMessageElement) {
            const messageHeight = lastMessageElement.offsetHeight
            const containerHeight = this.$refs.messagesContainer.clientHeight

            if (messageHeight > containerHeight) {
              const userQuestionElement = allMessages[allMessages.length - 2]
              if (userQuestionElement) {
                this.$refs.messagesContainer.scrollTop = userQuestionElement.offsetTop - 80
              } else {
                this.scrollToBottom()
              }
            } else {
              this.scrollToBottom()
            }
          } else {
            this.scrollToBottom()
          }
        }
      })
    },

    handleServerMessage(message) {
      console.log('📥 Mensagem recebida:', message.action, message.payload)

      switch (message.action) {
        case 'AUTHENTICATED':
          console.log('✅ Autenticado com sucesso!')
          break
          
        case 'TUTORIA_TYPING':
          this.isTyping = message.payload.typing
          break

        case 'TUTORIA_ANSWER':
          this.isTyping = false
          this.sessionId = message.payload.session_id
          
          this.conversationHistory.push({
            role: 'assistant',
            content: message.payload.answer,
            recommended_videos: message.payload.recommended_videos || [],
            timestamp: message.payload.timestamp
          })
          
          this.handleAnswerScroll()
          break

        case 'TUTORIA_SESSION_CREATED':
          this.sessionId = message.payload.session_id
          console.log('Nova sessão criada:', this.sessionId)
          break

        case 'TUTORIA_HISTORY':
          this.sessionId = message.payload.session_id
          this.conversationHistory = message.payload.conversation_history || []
          this.scrollToBottom()
          break

        case 'TUTORIA_SEARCH_RESULTS':
          this.isTyping = false
          this.conversationHistory.push({
            role: 'assistant',
            content: `Encontrei ${message.payload.total_results} resultado(s) para "${message.payload.query}"`,
            results: message.payload.results,
            timestamp: new Date().toISOString()
          })
          this.scrollToBottom()
          break

        case 'TUTORIA_ERROR':
          this.isTyping = false
          console.error('❌ Erro CyberTutor:', message.payload)
          this.conversationHistory.push({
            role: 'assistant',
            content: `⚠️ Erro: ${message.payload.message}`,
            timestamp: new Date().toISOString()
          })
          this.scrollToBottom()
          break
          
        default:
          console.log('ℹ️ Ação não tratada:', message.action)
      }
    },

    sendQuestion() {
      if (!this.currentQuestion.trim() || !this.isConnected || this.isTyping) {
        console.warn('⚠️ Não é possível enviar:', {
          questionEmpty: !this.currentQuestion.trim(),
          notConnected: !this.isConnected,
          isTyping: this.isTyping
        })
        return
      }

      const question = this.currentQuestion.trim()
      
      console.log('📤 Enviando pergunta:', question)

      this.conversationHistory.push({
        role: 'user',
        content: question,
        timestamp: new Date().toISOString()
      })
      
      this.isTyping = true
      this.scrollToBottom()

      const message = {
        action: 'TUTORIA_ASK_QUESTION',
        token: this.myToken,
        liveId: 'tutoria',
        payload: {
          question,
          current_class_id: this.currentClassId,
          max_results: 5
        }
      }
      
      console.log('📨 Mensagem enviada:', message)
      this.sendMessage(message)

      this.currentQuestion = ''
    },

    createNewSession() {
      if (!this.isConnected) return

      this.sendMessage({
        action: 'TUTORIA_NEW_SESSION',
        token: this.myToken,
        liveId: 'tutoria',
        payload: {}
      })

      this.conversationHistory = []
      this.sessionId = null
    },

    clearSession() {
      if (!this.isConnected) return

      this.sendMessage({
        action: 'TUTORIA_CLEAR_SESSION',
        token: this.myToken,
        liveId: 'tutoria',
        payload: {
          session_id: this.sessionId
        }
      })

      this.conversationHistory = []
    },

    getHistory() {
      if (!this.isConnected) return

      this.sendMessage({
        action: 'TUTORIA_GET_HISTORY',
        token: this.myToken,
        liveId: 'tutoria',
        payload: {
          session_id: this.sessionId
        }
      })
    },

    formatMessage(content) {
      if (!content) return ''
      
      return content
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
    },

    formatTime(timestamp) {
      if (!timestamp) return ''
      
      const date = new Date(timestamp)
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getUniqueVideos(videos) {
      if (!videos || videos.length === 0) return []
      
      const seen = new Map()
      const unique = []
      
      for (const video of videos) {
        const key = `${video.course_url}|${video.course_name}`
        if (!seen.has(key)) {
          seen.set(key, true)
          unique.push(video)
        }
      }
      
      return unique
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
        }
      })
    }
  },
  mounted() {
    console.log('🤖 CyberTutor Chat montado')
    console.log('📡 WebSocket URL:', this.wsUrl)
    console.log('🔑 Token disponível:', !!this.myToken)
    console.log('📚 Class ID:', this.currentClassId)
    
    this.connectWebSocket()
    
    const checkConnection = setInterval(() => {
      if (this.isConnected) {
        console.log('✅ Conexão estabelecida, buscando histórico...')
        this.getHistory()
        clearInterval(checkConnection)
      }
    }, 500)
    
    setTimeout(() => {
      clearInterval(checkConnection)
      if (!this.isConnected) {
        console.error('❌ Timeout: Não foi possível conectar ao WebSocket')
      }
    }, 10000)
  },
  beforeDestroy() {
    if (this.ws) {
      this.ws.close()
    }
  }
}
</script>

<style lang="scss">
.tutoria-chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.tutoria-chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  &-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
}

.tutoria-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;

  &-icon {
    width: 28px;
    height: 28px;
  }
}

.tutoria-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
}

.tutoria-status {
  font-size: 0.75rem;
  opacity: 0.8;
  margin: 0;
  
  &.connected {
    opacity: 1;
    font-weight: 500;
  }
}

.tutoria-close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;

  span {
    font-size: 2rem !important;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.tutoria-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #f7f9fc;
}

.tutoria-not-authenticated {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #64748b;
  
  .tutoria-not-authenticated-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.7;
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #334155;
    margin: 0 0 1rem 0;
  }
  
  p {
    margin: 0.5rem 0;
    line-height: 1.6;
    
    &:first-of-type {
      font-weight: 500;
      color: #475569;
    }
  }
}

.tutoria-empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
  
  p {
    margin: 0.5rem 0;
    
    &:first-child {
      font-size: 1.125rem;
      font-weight: 500;
    }
  }
}

.tutoria-message {
  display: flex;
  
  &.user {
    justify-content: flex-end;
    
    .tutoria-message-content {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 18px 18px 4px 18px;
    }
  }
  
  &.assistant {
    justify-content: flex-start;
    
    .tutoria-message-content {
      background: white;
      color: #1e293b;
      border-radius: 18px 18px 18px 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    }
  }
}

.tutoria-message-content {
  max-width: 80%;
  padding: 0.875rem 1.125rem;
}

.tutoria-message-text {
  margin-bottom: 0.5rem;
  line-height: 1.5;
  word-wrap: break-word;
  
  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: #667eea;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.tutoria-message-time {
  font-size: 0.7rem;
  opacity: 0.7;
  margin-top: 0.5rem;
}

.tutoria-recommended-videos {
  margin-top: 0.875rem;
  padding-top: 0.875rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.tutoria-recommended-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.tutoria-video-item {
  margin: 0.5rem 0;
  font-size: 0.875rem;
  
  a {
    color: #667eea;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.tutoria-typing-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tutoria-typing-text {
  font-size: 0.875rem;
  color: #64748b;
}

.tutoria-typing-dots {
  display: flex;
  gap: 0.25rem;
  
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #64748b;
    animation: typing 1.4s infinite;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

.tutoria-input-container {
  padding: 1rem 1.25rem;
  background: white;
  border-top: 1px solid #e2e8f0;
  position: relative;
}

.tutoria-input-form {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
}

.tutoria-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 24px;
  font-size: 0.9375rem;
  resize: none;
  max-height: 120px;
  font-family: inherit;
  transition: border-color 0.2s;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }
}

.tutoria-send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  }
}

.tutoria-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  justify-content: center;
}

.tutoria-action-btn {
  padding: 0.5rem 0.875rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  color: black;
  
  &:hover:not(:disabled) {
    background: #e2e8f0;
    border-color: #cbd5e1;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.tutoria-toggle-mode-btn {
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  opacity: 0.5;
  z-index: 10;
  
  &:hover {
    opacity: 1;
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  span {
    display: inline-block;
    transform: rotate(90deg);
  }
}
</style>
